import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './FilePreviewModal.scss';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
    getFileDownload,
    getFilePreview,
} from '../../../api/actions/Documents';
import { DownloadOutline } from 'react-ionicons';

const FilePreviewModal = ({ show, onClose, file, profile, session }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [fileUrl, setFileUrl] = useState();

    const onDisplayError = useCallback((e) => {
        toast.error(
            'Something went wrong while loading the preview. Please try again.',
            {
                className: 'custom-toast',
            }
        );
        setShowError(true);
    }, []);

    const content = useMemo(() => {
        if (!fileUrl) {
            return null;
        }

        switch (file.fileExtension) {
            case 'image/png':
            case 'image/jpg':
            case 'image/jpeg':
            case '.png':
            case '.jpeg':
            case '.jpg':
            case '.hec':
                return (
                    <img
                        alt="file"
                        className="file-element-preview"
                        src={fileUrl}
                        style={{
                            width: '100%',
                            minHeight: '310px',
                            maxHeight: '70vh',
                            height: 'auto',
                            objectFit: 'contain',
                        }}
                        onLoad={() => setIsLoading(false)}
                        onError={onDisplayError}
                    />
                );
            case '.pdf':
            case 'application/pdf':
                return (
                    <iframe
                        title="file preview"
                        className="file-element-preview"
                        src={fileUrl}
                        onLoad={() => setIsLoading(false)}
                        onError={onDisplayError}
                    />
                );

            default:
                setIsLoading(false);
                return (
                    <div className="d-flex justify-content-center my-5">
                        <p className="text-center">
                            No preview available for this file at the moment.
                        </p>
                    </div>
                );
        }
    }, [file, fileUrl, onDisplayError]);

    const fetchUrl = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = getFilePreview(profile.entityId, file.id);

            setFileUrl(response);
        } catch (error) {
            console.error(error);
            setShowError(true);
            toast.error(
                'Something went wrong while loading the preview. Please try again.',
                {
                    className: 'custom-toast',
                }
            );
        } finally {
            setIsLoading(false);
        }
    }, [profile, file]);

    useEffect(() => {
        if (show) {
            fetchUrl();
        } else {
            setShowError(false);
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const onDownload = useCallback(
        async (e) => {
            if (e && e.preventDefault) {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
            }

            try {
                const url =
                    getFileDownload(profile.entityId, file.id) +
                    '&download=true';
                window.open(url, '_blank');
            } catch (error) {
                console.error('Error downloading file:', error);
                toast.error(
                    'Something wrong happened while downloading the file. Please try again.',
                    {
                        className: 'custom-toast',
                    }
                );
            }
        },
        [file, profile]
    );

    return (
        <Modal show={show} size="xl" onHide={onClose}>
            <Modal.Header closeButton>File preview</Modal.Header>
            <Modal.Body>
                <div style={{ minHeight: 100 }}>
                    {isLoading && !showError && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                            }}
                            className="d-flex justify-content-center align-items-center icon my-5"
                        >
                            <Spinner />
                        </div>
                    )}

                    {showError ? (
                        <div className="d-flex justify-content-center my-5">
                            <p className="text-center">
                                No preview available for this file at the
                                moment.
                            </p>
                        </div>
                    ) : (
                        content
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    id="close-button"
                    onClick={onClose}
                >
                    Close
                </Button>

                <Button
                    onClick={onDownload}
                    className="d-flex align-items-center"
                >
                    <DownloadOutline
                        className="me-2"
                        color="white"
                        width={'24px'}
                        height={'24px'}
                    />
                    <p>Download</p>
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const mapStateToProps = (appState) => ({
    profile: appState.userProfile,
    session: appState.session,
});

export default connect(mapStateToProps)(FilePreviewModal);
